/*
 * Sube un fichero a S3 por partes usando las URLs prefirmadas que entrega
 * `uploadService` (por defecto VideoUploadService). Depende de esa interfaz
 * (initiate/signPart/complete/abort) y no de la API concreta.
 *
 * Las partes se suben con XMLHttpRequest y no con axios: la instancia global
 * lleva la cabecera Authorization de la API y S3 rechaza la petición firmada
 * si recibe otra credencial. El bucket debe exponer la cabecera ETag por CORS.
 */
const DEFAULT_CHUNK_SIZE = 10 * 1024 * 1024 // 10 MB (S3 exige ≥ 5 MB salvo en la última parte)

const putPart = (url, blob, onProgress) => new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('PUT', url)
    request.upload.onprogress = (event) => onProgress(event.loaded)
    request.onload = () => {
        if (request.status >= 200 && request.status < 300) {
            resolve(request.getResponseHeader('ETag'))
        } else {
            reject(new Error('S3 part upload failed with status ' + request.status))
        }
    }
    request.onerror = () => reject(new Error('S3 part upload network error'))
    request.send(blob)
})

export default class S3MultipartUploader {
    constructor(uploadService, {chunkSize = DEFAULT_CHUNK_SIZE} = {}) {
        this.uploadService = uploadService
        this.chunkSize = chunkSize
    }

    // onProgress(bytesUploaded, bytesTotal). Resuelve con la respuesta de `complete`.
    async upload(file, {onProgress = () => {}} = {}) {
        const {data: upload} = await this.uploadService.initiate(file)
        const uploadId = upload.upload_id
        const key = upload.key
        const uploadedByPart = []
        const parts = []

        try {
            const partCount = Math.max(1, Math.ceil(file.size / this.chunkSize))
            for (let index = 0; index < partCount; index++) {
                const partNumber = index + 1
                const blob = file.slice(index * this.chunkSize, Math.min(file.size, partNumber * this.chunkSize))
                const {data: signed} = await this.uploadService.signPart(uploadId, key, partNumber)

                const etag = await putPart(signed.url, blob, (loaded) => {
                    uploadedByPart[index] = loaded
                    onProgress(uploadedByPart.reduce((total, bytes) => total + (bytes ?? 0), 0), file.size)
                })
                parts.push({part_number: partNumber, etag})
            }

            const {data} = await this.uploadService.complete(uploadId, key, parts)
            return data?.data ?? data
        } catch (error) {
            this.uploadService.abort(uploadId, key).catch(() => {})
            throw error
        }
    }
}
